import React from 'react';
import { Box, Typography, Button, Card, CardContent, useTheme } from '@mui/material';
import EventCountdown from '../../util/EventCountdown';
import { League, LeaguePhase } from '../../../sdk/model';

interface EventSectionProps {
  league: League;
  leagueEvent: any; // TODO: Add proper type
  preparingPhase: LeaguePhase;
  eventType: string;
  deadlineEvents: string;
  activePhases: LeaguePhase[];
  roomUrl: string;
}

const EventSection: React.FC<EventSectionProps> = ({
  league,
  leagueEvent,
  preparingPhase,
  eventType,
  deadlineEvents,
  activePhases,
  roomUrl,
}) => {
  const eventDate = leagueEvent?.eventDateTime ? new Date(leagueEvent.eventDateTime) : null;
  const ACTIVE_EVENT_STATUSES = new Set(['PENDING', 'STARTED', 'PAUSED']);

  const theme = useTheme();

  if (league?.phase === preparingPhase && eventDate) {
    return leagueEvent?.eventDateTime ? (
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="h6" component="h3" sx={{ mb: 1.5, fontWeight: 'medium' }}>
            {eventType} Countdown
          </Typography>
          <Box sx={{ 
            background: theme => `linear-gradient(135deg, ${theme.palette.secondary.light}10, ${theme.palette.secondary.dark}10)`,
            p: 2,
            borderRadius: 1,
            borderLeft: theme => `4px solid ${theme.palette.secondary.main}`,
            mb: 2
          }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              The {league?.currentSeason?.seasonYear} {eventType} is scheduled for {eventDate.toLocaleDateString()} at{' '}
              {eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
              <br /><br />
              The deadline for {deadlineEvents} is 72 hours prior. This is also when rosters will lock in
              preparation for the {eventType}.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 3,
              '& > div': {
                flex: 1,
                textAlign: 'center',
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }
            }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Roster Deadline
                </Typography>
                <Typography variant="h6" color="primary">
                  <EventCountdown dateTime={league.phaseTransitionDateTime} />
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {eventType} Starts In
                </Typography>
                <Typography variant="h6" color="primary">
                  <EventCountdown dateTime={leagueEvent.eventDateTime} />
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    ) : (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h3" sx={{ mb: 1.5, fontWeight: 'medium' }}>
            {eventType} Information
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The {eventType} date/time has not yet been set. Please check back later.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (activePhases.includes(league?.phase) && eventDate) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="h6" component="h3" sx={{ mb: 1.5, fontWeight: 'medium' }}>
            {eventType}
          </Typography>
          <Box sx={{ 
            background: theme => `linear-gradient(135deg, ${theme.palette.success.light}10, ${theme.palette.success.dark}10)`,
            p: 2,
            borderRadius: 1,
            borderLeft: theme => `4px solid ${theme.palette.success.main}`,
            mb: 2
          }}>
            {ACTIVE_EVENT_STATUSES.has(leagueEvent.status) ? <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              The {league?.currentSeason?.seasonYear} {eventType} is currently active.
              <br /><br />
              The room will remain open until the event concludes.
            </Typography> :
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              The {league?.currentSeason?.seasonYear} {eventType} room will open 1 hour prior to
              the start of the event.
            </Typography>}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 3,
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 3
            }}>
              <Box sx={{ 
                flex: 1,
                textAlign: 'center',
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Time Remaining
                </Typography>
                <Typography variant="h6" color="primary">
                  <EventCountdown dateTime={leagueEvent.eventDateTime} />
                </Typography>
              </Box>
              {ACTIVE_EVENT_STATUSES.has(leagueEvent.status) && (
                <Button 
                  variant="contained" 
                  color="primary"
                  size="large"
                  onClick={() => window.open(roomUrl + league?.id, '_blank', 'noreferrer')}
                  sx={{ 
                    minWidth: 200,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5
                  }}
                >
                  Enter {eventType} Room
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default EventSection;
